import React, { Component, type ReactNode } from 'react'
import { Box, VStack, Text, Button, Heading } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { isChunkLoadError, reloadForStaleChunk } from '../utils/chunkReload'

interface ErrorBoundaryProps {
  children: ReactNode
  translations?: {
    title: string
    message: string
    tryAgain: string
    goHome: string
  }
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  resetKey: number
}

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: 0
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // For stale-deploy chunk errors, suppress the fallback UI — componentDidCatch
    // will reload the page. Keep hasError:true so React doesn't re-attempt rendering
    // the broken child tree.
    return { hasError: true, error, errorInfo: null, resetKey: 0 }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)

    // Stale deploy: a lazy route chunk from the previous build is gone, so the
    // dynamic import failed (any of several browser-specific messages, or the
    // SPA index.html fallback served as text/html). Auto-reload once to pick up
    // the new bundle rather than showing the error UI.
    if (isChunkLoadError(error)) {
      reloadForStaleChunk()
      return
    }

    this.setState({ error, errorInfo })
  }

  handleReset = () => {
    window.location.reload()
  }

  render() {
    const { translations } = this.props

    const isChunkError = isChunkLoadError(this.state.error)

    if (this.state.hasError && !isChunkError) {
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="bg.subtle"
          p={4}
        >
          <VStack
            gap={6}
            maxW="600px"
            w="full"
            bg="white"
            p={8}
            borderRadius="lg"
            shadow="sm"
            border="1px"
            borderColor="border.muted"
          >
            <VStack gap={3} textAlign="center">
              <Heading size="lg" color="red.600">
                {translations?.title || 'Oops! Something went wrong'}
              </Heading>
              <Text color="fg.muted">
                {translations?.message || 'We apologize for the inconvenience. The application encountered an unexpected error.'}
              </Text>
            </VStack>

            {import.meta.env.DEV && this.state.error && (
              <Box
                w="full"
                p={4}
                bg="gray.50"
                borderRadius="md"
                fontFamily="mono"
                fontSize="sm"
                overflowX="auto"
              >
                <Text fontWeight="bold" color="red.700" mb={2}>
                  {this.state.error.toString()}
                </Text>
                {this.state.errorInfo && (
                  <Text whiteSpace="pre-wrap" color="gray.700">
                    {this.state.errorInfo.componentStack}
                  </Text>
                )}
              </Box>
            )}

            <VStack gap={3} w="full">
              <Button
                colorScheme="brand"
                onClick={this.handleReset}
                w="full"
              >
                {translations?.tryAgain || 'Try Again'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => window.location.href = '/'}
                w="full"
              >
                {translations?.goHome || 'Go to Home Page'}
              </Button>
            </VStack>
          </VStack>
        </Box>
      )
    }

    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>
  }
}

// Wrapper component that uses hooks to provide translations
export const ErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  
  const translations = {
    title: t('errors.unexpected_error_title'),
    message: t('errors.unexpected_error_message'),
    tryAgain: t('errors.try_again_button'),
    goHome: t('errors.go_home')
  }
  
  return (
    <ErrorBoundaryClass translations={translations}>
      {children}
    </ErrorBoundaryClass>
  )
}